<template>
  <div class="tasks-board">
    <div class="board-shell">
      <header class="board-header">
        <div class="board-title">
          <div class="title-row">
            <h1>Tablero de tareas</h1>
            <span class="board-count">{{ visibleTasksCount }} tareas</span>
          </div>
          <p class="muted">
            Arrastra tarjetas para priorizar y mantente en foco con filtros rapidos.
          </p>
          <div class="board-meta">
            <span class="meta-chip">Proyecto: {{ activeProjectLabel }}</span>
            <span class="meta-chip">Total: {{ totalTasksCount }}</span>
            <span v-if="activeFilters.length" class="meta-chip active">
              Filtros: {{ activeFilters.length }}
            </span>
          </div>
          <div class="quick-filters" role="list">
            <button
              type="button"
              class="chip"
              :class="{ active: dateFilter === 'overdue' }"
              @click="applyQuickFilter('overdue')"
            >
              Vencidas
            </button>
            <button
              type="button"
              class="chip"
              :class="{ active: dateFilter === 'today' }"
              @click="applyQuickFilter('today')"
            >
              Hoy
            </button>
            <button
              type="button"
              class="chip"
              :class="{ active: dateFilter === 'week' }"
              @click="applyQuickFilter('week')"
            >
              Esta semana
            </button>
            <button
              type="button"
              class="chip"
              :class="{ active: selectedPriority === 'high' }"
              @click="applyQuickFilter('high')"
            >
              Prioridad alta
            </button>
          </div>
        </div>
        <div class="board-actions">
          <div class="view-toggle" role="tablist" aria-label="Vista del tablero">
            <button
              type="button"
              class="toggle"
              :class="{ active: currentView === 'kanban' }"
              role="tab"
              :aria-selected="currentView === 'kanban'"
              @click="setView('kanban')"
            >
              Kanban
            </button>
            <button
              type="button"
              class="toggle"
              :class="{ active: currentView === 'list' }"
              role="tab"
              :aria-selected="currentView === 'list'"
              @click="setView('list')"
            >
              Lista
            </button>
            <button
              type="button"
              class="toggle"
              :class="{ active: currentView === 'calendar' }"
              role="tab"
              :aria-selected="currentView === 'calendar'"
              @click="setView('calendar')"
            >
              Calendario
            </button>
            <button
              type="button"
              class="toggle"
              :class="{ active: compactMode }"
              role="tab"
              :aria-selected="compactMode"
              @click="toggleCompact"
            >
              Compacta
            </button>
          </div>
          <div class="action-row">
            <button class="primary" @click="openTaskModal()">Nueva tarea</button>
            <div class="lane-create">
              <input
                v-model="newLaneName"
                type="text"
                placeholder="Nuevo carril"
                @keyup.enter="createNewLane"
              />
              <button class="ghost" @click="createNewLane">Crear carril</button>
            </div>
          </div>
        </div>
      </header>

      <KanbanToolbar
        :search-query="searchQuery"
        :projects="projects"
        :selected-project-id="selectedProjectId"
        :available-tags="availableTags"
        :selected-tag="selectedTag"
        :selected-priority="selectedPriority"
        :date-filter="dateFilter"
        :sort-mode="sortMode"
        :active-filters="activeFilters"
        @update:searchQuery="setSearchQuery"
        @update:selectedProjectId="setProject"
        @update:selectedTag="setTag"
        @update:selectedPriority="setPriority"
        @update:dateFilter="setDateFilter"
        @update:sortMode="setSortMode"
        @clear-filter="clearFilter"
        @clear-filters="clearFilters"
      />
    </div>

    <section v-if="loading" class="lanes skeleton-board" role="status" aria-live="polite">
      <span class="sr-only">Cargando tablero...</span>
      <div class="lanes-inner">
        <div v-for="n in 3" :key="`lane-skel-${n}`" class="lane lane-skeleton">
          <div class="lane-header">
            <div class="lane-title">
              <div class="skeleton skeleton-line" style="width: 120px"></div>
              <div class="skeleton skeleton-line" style="width: 32px; height: 20px"></div>
            </div>
            <div class="lane-actions">
              <div class="skeleton skeleton-line" style="width: 28px; height: 28px"></div>
              <div class="skeleton skeleton-line" style="width: 28px; height: 28px"></div>
            </div>
          </div>
          <div class="cards">
            <div v-for="i in 3" :key="`card-skel-${n}-${i}`" class="card-skeleton">
              <div class="skeleton skeleton-line lg" style="width: 78%"></div>
              <div class="skeleton skeleton-line" style="width: 56%"></div>
              <div class="skeleton skeleton-line" style="width: 40%"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="!loading && currentView === 'list'" class="list-view">
      <div class="list-table">
        <div class="list-row header">
          <span>Tarea</span>
          <span>Carril</span>
          <span>Prioridad</span>
          <span>Fecha limite</span>
          <span>Responsable</span>
          <span>Progreso</span>
          <span>Actualizada</span>
          <span></span>
        </div>
        <div
          v-for="task in listTasks"
          :key="task.id"
          class="list-row"
          role="button"
          tabindex="0"
          @click="openTaskModal(task.laneId, task)"
          @keydown.enter="openTaskModal(task.laneId, task)"
        >
          <span class="cell-title">
            <strong>{{ task.title }}</strong>
            <small v-if="task.projectName">{{ task.projectName }}</small>
          </span>
          <span class="cell-lane">
            <span class="lane-pill" :class="laneTone(task.laneName)">{{ task.laneName }}</span>
          </span>
          <span>
            <span class="priority-pill" :class="priorityClass(task)">
              {{ priorityLabel(task) || '—' }}
            </span>
          </span>
          <span :class="{ overdue: isOverdue(task) }">{{ formatDueDate(task) || '—' }}</span>
          <span>{{ taskAssignee(task) || '—' }}</span>
          <span>{{ taskProgress(task) != null ? `${taskProgress(task)}%` : '—' }}</span>
          <span>{{ formatDate(task.updated || task.created) }}</span>
          <span class="row-actions">
            <button class="icon tiny" type="button" @click.stop="duplicateTask(task.laneId, task)">
              Dup
            </button>
          </span>
        </div>
        <div v-if="!listTasks.length" class="state">
          No hay tareas con los filtros actuales.
        </div>
      </div>
    </section>

    <section v-if="!loading && currentView === 'calendar'" class="calendar-view">
      <div class="calendar-header">
        <div class="calendar-controls">
          <button class="ghost tiny" type="button" @click="shiftCalendar(-1)">Anterior</button>
          <button class="ghost tiny" type="button" @click="resetCalendar">Hoy</button>
          <button class="ghost tiny" type="button" @click="shiftCalendar(1)">Siguiente</button>
        </div>
        <h3>{{ calendarLabel }}</h3>
      </div>
      <div class="calendar-grid">
        <div v-for="day in weekDays" :key="day" class="calendar-weekday">
          {{ day }}
        </div>
        <div
          v-for="day in calendarDays"
          :key="day.key"
          class="calendar-cell"
          :class="{ muted: !day.isCurrentMonth, today: day.isToday }"
        >
          <div class="cell-head">{{ day.label }}</div>
          <div class="cell-tasks">
            <button
              v-for="task in day.tasks"
              :key="task.id"
              type="button"
              class="calendar-pill"
              :class="[priorityClass(task), { overdue: isOverdue(task) }]"
              @click="openTaskModal(task.laneId, task)"
            >
              {{ task.title }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="unscheduledTasks.length" class="calendar-unscheduled">
        <h4>Sin fecha</h4>
        <div class="unscheduled-list">
          <button
            v-for="task in unscheduledTasks"
            :key="task.id"
            type="button"
            class="calendar-pill ghost-pill"
            @click="openTaskModal(task.laneId, task)"
          >
            {{ task.title }}
          </button>
        </div>
      </div>
    </section>

    <div
      v-if="!loading && currentView === 'kanban' && filteredLanes.length"
      class="board-scroll"
      @dragover.prevent
      @drop="onLaneDrop"
    >
      <div class="lanes-inner">
        <div
          v-for="(lane, laneIndex) in filteredLanes"
          :key="lane.id"
          class="lane"
          :class="[lane.tone, { collapsed: isLaneCollapsed(lane.id), 'drop-target': isLaneDragTarget(laneIndex) }]"
          :draggable="canDrag && !draggingTask"
          @dragstart="onLaneDragStart(lane.id, $event)"
          @dragend="onLaneDragEnd"
          @dragover.prevent="onLaneDragOver(laneIndex)"
        >
          <div class="lane-header">
            <div class="lane-title">
              <span class="lane-dot" aria-hidden="true"></span>
              <input
                v-if="editingLaneId === lane.id"
                v-model="editingLaneName"
                @keyup.enter="saveLaneName(lane)"
                @blur="saveLaneName(lane)"
                class="lane-input"
              />
              <h3 v-else @dblclick="editLane(lane)">{{ lane.name }}</h3>
              <span class="count">{{ lane.visibleCount }}</span>
              <span v-if="lane.totalCount !== lane.visibleCount" class="count subtle">
                / {{ lane.totalCount }}
              </span>
            </div>
            <div class="lane-actions">
              <button
                class="icon"
                type="button"
                :aria-label="isLaneCollapsed(lane.id) ? 'Expandir carril' : 'Colapsar carril'"
                @click="toggleLaneCollapse(lane.id)"
              >
                {{ isLaneCollapsed(lane.id) ? '+' : '-' }}
              </button>
              <button class="icon" type="button" @click="openTaskModal(lane.id)">+</button>
              <button class="icon danger" type="button" @click="removeLane(lane)">×</button>
            </div>
          </div>

          <div
            class="lane-body"
            :class="{ collapsed: isLaneCollapsed(lane.id), 'drag-over': isDragOverLane(lane.id) }"
            @dragover.prevent
            @drop="onTaskDrop(lane.id)"
          >
            <transition-group name="card" tag="div" class="cards">
              <KanbanCard
                v-for="(task, taskIndex) in lane.tasks"
                :key="task.id"
                :task="task"
                :draggable="canDrag"
                :is-dragging="isDragging(task, lane)"
                :compact="compactMode"
                :priority-label="priorityLabel(task)"
                :priority-class="priorityClass(task)"
                :tags="taskTags(task)"
                :due-label="formatDueDate(task)"
                :overdue="isOverdue(task)"
                :assignee-label="taskAssignee(task)"
                :progress="taskProgress(task)"
                @dragstart="onTaskDragStart(task, lane.id, $event)"
                @dragend="onTaskDragEnd"
                @dragover="onTaskDragOver(lane.id, taskIndex)"
                @open="openTaskModal(lane.id, task)"
                @toggle-done="toggleDone(task)"
                @duplicate="duplicateTask(lane.id, task)"
              />
            </transition-group>
            <div class="lane-empty" v-if="!lane.tasks.length">
              <p v-if="hasActiveFilters">Sin coincidencias. Ajusta filtros.</p>
              <p v-else>Arrastra aqui o crea una tarea.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showTaskModal" class="modal-backdrop" @click.self="closeTaskModal">
      <div class="modal">
        <header>
          <h3>{{ taskForm.id ? "Editar tarea" : "Nueva tarea" }}</h3>
          <button class="icon danger" @click="closeTaskModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Titulo
            <input v-model="taskForm.title" type="text" placeholder="Titulo de la tarea" />
          </label>
          <label>
            Descripcion
            <textarea v-model="taskForm.description" rows="4" placeholder="Detalle..."></textarea>
          </label>
          <div class="modal-grid">
            <label>
              Prioridad
              <select v-model="taskForm.priority">
                <option value="">Sin prioridad</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </select>
            </label>
            <label>
              Fecha limite
              <input v-model="taskForm.dueDate" type="date" />
            </label>
          </div>
          <label>
            Tags
            <input v-model="taskForm.tagsInput" type="text" placeholder="diseno, frontend, urgente" />
          </label>
          <div class="modal-grid">
            <label>
              Responsable
              <input v-model="taskForm.assigneeName" type="text" placeholder="Nombre del responsable" />
            </label>
            <label>
              Progreso ({{ taskForm.progress }}%)
              <input v-model.number="taskForm.progress" type="range" min="0" max="100" step="5" />
            </label>
          </div>
          <label>
            Carril
            <select v-model="taskForm.laneId">
              <option v-for="lane in lanes" :key="lane.id" :value="lane.id">
                {{ lane.name }}
              </option>
            </select>
          </label>
          <label>
            Proyecto
            <div class="project-field">
              <input
                v-model="projectSearch"
                placeholder="Busca o crea un proyecto"
                @input="onProjectInput"
                @focus="openProjectOptions"
                @blur="closeProjectOptions"
              />
              <button class="ghost" @click="createProjectFromInput">
                Crear
              </button>
              <div v-if="showProjectOptions" class="project-options">
                <button
                  v-for="project in projectSuggestions"
                  :key="project.id"
                  type="button"
                  class="project-option"
                  @mousedown.prevent="selectProject(project)"
                >
                  {{ project.name }}
                </button>
                <div v-if="!projectSuggestions.length" class="project-empty">
                  Sin coincidencias
                </div>
              </div>
            </div>
          </label>
          <label class="checkbox line">
            <input type="checkbox" :checked="taskForm.done" @change="toggleFormDone" />
            <span></span>
            Marcar como completada
          </label>
        </div>
        <footer>
          <button class="ghost" @click="closeTaskModal">Cancelar</button>
          <button class="primary" @click="saveTask">{{ taskForm.id ? "Guardar" : "Crear" }}</button>
        </footer>
        <button v-if="taskForm.id" class="danger-text" @click="removeTask">
          Eliminar tarea
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchTaskBoard,
  fetchTaskProjects,
  createTaskProject,
  createLane,
  updateLane,
  deleteLane,
  reorderLanes,
  createTask,
  updateTask,
  deleteTask,
  reorderTasks,
} from "../services/tasks";
import KanbanCard from "../components/kanban/KanbanCard.vue";
import KanbanToolbar from "../components/kanban/KanbanToolbar.vue";

const PREF_KEY = "cerebro_kanban_prefs";

export default {
  name: "TasksBoardView",
  components: { KanbanCard, KanbanToolbar },
  data() {
    return {
      lanes: [],
      projects: [],
      selectedProjectId: null,
      projectSearch: "",
      showProjectOptions: false,
      loading: false,
      error: "",
      newLaneName: "",
      editingLaneId: null,
      editingLaneName: "",
      draggingTask: null,
      dragOver: null,
      draggingLaneId: null,
      laneDropIndex: null,
      dragOverLaneId: null,
      showTaskModal: false,
      taskForm: {
        id: null,
        title: "",
        description: "",
        laneId: null,
        done: false,
        projectId: null,
        priority: "",
        tagsInput: "",
        dueDate: "",
        assigneeName: "",
        progress: 0,
      },
      searchQuery: "",
      searchQueryDebounced: "",
      selectedTag: "",
      selectedPriority: "all",
      dateFilter: "any",
      sortMode: "manual",
      currentView: "kanban",
      compactMode: false,
      collapsedLaneIds: [],
      calendarMonth: "",
      searchTimer: null,
    };
  },
  async mounted() {
    this.loadPreferences();
    await this.loadBoard();
  },
  computed: {
    canDrag() {
      return this.currentView === "kanban" && this.sortMode === "manual";
    },
    activeProjectLabel() {
      if (!this.selectedProjectId) return "Todos";
      const match = this.projects.find((project) => String(project.id) === String(this.selectedProjectId));
      return match ? match.name : "Todos";
    },
    availableTags() {
      const tags = new Set();
      this.lanes.forEach((lane) => {
        lane.tasks.forEach((task) => {
          this.taskTags(task).forEach((tag) => tags.add(tag));
        });
      });
      return Array.from(tags).sort((a, b) => a.localeCompare(b));
    },
    flatTasks() {
      const list = [];
      this.lanes.forEach((lane, laneIndex) => {
        lane.tasks.forEach((task) => {
          list.push({
            ...task,
            laneId: task.laneId ?? lane.id,
            laneName: lane.name,
            laneIndex,
          });
        });
      });
      return list;
    },
    listTasks() {
      const visible = this.filterTasks(this.flatTasks);
      if (this.sortMode === "manual") {
        return [...visible].sort((a, b) => {
          if (a.laneIndex !== b.laneIndex) return a.laneIndex - b.laneIndex;
          return (a.position ?? 0) - (b.position ?? 0);
        });
      }
      return this.sortTasks(visible, this.sortMode);
    },
    weekDays() {
      return ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
    },
    calendarMonthDate() {
      if (this.calendarMonth) {
        const [year, month] = this.calendarMonth.split("-").map(Number);
        if (year && month) return new Date(year, month - 1, 1);
      }
      return new Date();
    },
    calendarLabel() {
      const date = this.calendarMonthDate;
      return new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(date);
    },
    calendarDays() {
      const monthDate = this.calendarMonthDate;
      const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const startDay = (start.getDay() + 6) % 7;
      const gridStart = new Date(start);
      gridStart.setDate(start.getDate() - startDay);
      const tasks = this.filterTasks(this.flatTasks).filter((task) => this.parseDate(task.dueDate));
      const tasksByKey = tasks.reduce((acc, task) => {
        const key = this.toDateKey(this.parseDate(task.dueDate));
        if (!key) return acc;
        if (!acc[key]) acc[key] = [];
        acc[key].push(task);
        return acc;
      }, {});
      Object.values(tasksByKey).forEach((list) => {
        list.sort((a, b) => this.priorityRank(b) - this.priorityRank(a));
      });
      const days = [];
      for (let i = 0; i < 42; i += 1) {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + i);
        const key = this.toDateKey(date);
        days.push({
          key,
          date,
          label: date.getDate(),
          isCurrentMonth: date.getMonth() === monthDate.getMonth(),
          isToday: this.isToday(date),
          tasks: tasksByKey[key] || [],
        });
      }
      return days;
    },
    unscheduledTasks() {
      return this.filterTasks(this.flatTasks).filter((task) => !this.parseDate(task.dueDate));
    },
    filteredLanes() {
      return this.lanes.map((lane) => {
        const visibleTasks = this.filterTasks(lane.tasks);
        const sorted = this.sortTasks(visibleTasks, this.sortMode);
        return {
          ...lane,
          tasks: sorted,
          totalCount: lane.tasks.length,
          visibleCount: visibleTasks.length,
          tone: this.laneTone(lane),
        };
      });
    },
    totalTasksCount() {
      return this.lanes.reduce((sum, lane) => sum + lane.tasks.length, 0);
    },
    visibleTasksCount() {
      return this.filteredLanes.reduce((sum, lane) => sum + lane.visibleCount, 0);
    },
    activeFilters() {
      const filters = [];
      if (this.searchQueryDebounced.trim()) {
        filters.push({ key: "search", label: `Buscar: ${this.searchQueryDebounced}` });
      }
      if (this.selectedProjectId) {
        filters.push({ key: "project", label: `Proyecto: ${this.activeProjectLabel}` });
      }
      if (this.selectedTag) {
        filters.push({ key: "tag", label: `Tag: ${this.selectedTag}` });
      }
      if (this.selectedPriority !== "all") {
        filters.push({ key: "priority", label: `Prioridad: ${this.selectedPriority}` });
      }
      if (this.dateFilter !== "any") {
        filters.push({ key: "date", label: `Fecha: ${this.dateFilter}` });
      }
      if (this.sortMode !== "manual") {
        filters.push({ key: "sort", label: `Orden: ${this.sortMode}` });
      }
      return filters;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0;
    },
    projectSuggestions() {
      const query = this.projectSearch.trim().toLowerCase();
      const list = this.projects || [];
      if (!query) return list.slice(0, 6);
      return list
        .filter((project) => project.name.toLowerCase().includes(query))
        .slice(0, 6);
    },
  },
  watch: {
    searchQuery(value) {
      this.queueSearch(value);
      this.persistPreferences();
    },
    selectedProjectId() {
      this.persistPreferences();
    },
    selectedTag() {
      this.persistPreferences();
    },
    selectedPriority() {
      this.persistPreferences();
    },
    dateFilter() {
      this.persistPreferences();
    },
    sortMode() {
      this.persistPreferences();
    },
    currentView() {
      this.persistPreferences();
    },
    compactMode() {
      this.persistPreferences();
    },
    calendarMonth() {
      this.persistPreferences();
    },
  },
  methods: {
    loadPreferences() {
      try {
        const raw = window.localStorage.getItem(PREF_KEY);
        if (!raw) return;
        const prefs = JSON.parse(raw);
        this.selectedProjectId = prefs.selectedProjectId ?? null;
        this.searchQuery = prefs.searchQuery ?? "";
        this.searchQueryDebounced = prefs.searchQuery ?? "";
        this.selectedTag = prefs.selectedTag ?? "";
        this.selectedPriority = prefs.selectedPriority ?? "all";
        this.dateFilter = prefs.dateFilter ?? "any";
        this.sortMode = prefs.sortMode ?? "manual";
        this.currentView = prefs.currentView ?? "kanban";
        this.compactMode = prefs.compactMode ?? false;
        this.collapsedLaneIds = prefs.collapsedLaneIds ?? [];
        this.calendarMonth = prefs.calendarMonth ?? "";
      } catch (_) {
        // ignore broken prefs
      }
    },
    persistPreferences() {
      const payload = {
        selectedProjectId: this.selectedProjectId,
        searchQuery: this.searchQuery,
        selectedTag: this.selectedTag,
        selectedPriority: this.selectedPriority,
        dateFilter: this.dateFilter,
        sortMode: this.sortMode,
        currentView: this.currentView,
        compactMode: this.compactMode,
        collapsedLaneIds: this.collapsedLaneIds,
        calendarMonth: this.calendarMonth,
      };
      window.localStorage.setItem(PREF_KEY, JSON.stringify(payload));
    },
    queueSearch(value) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.searchQueryDebounced = value;
      }, 200);
    },
    setSearchQuery(value) {
      this.searchQuery = value;
    },
    setProject(value) {
      this.selectedProjectId = value;
    },
    setTag(value) {
      this.selectedTag = value;
    },
    setPriority(value) {
      this.selectedPriority = value;
    },
    setDateFilter(value) {
      this.dateFilter = value;
    },
    setSortMode(value) {
      this.sortMode = value;
    },
    clearFilter(filter) {
      if (!filter) return;
      if (filter.key === "search") this.searchQuery = "";
      if (filter.key === "project") this.selectedProjectId = null;
      if (filter.key === "tag") this.selectedTag = "";
      if (filter.key === "priority") this.selectedPriority = "all";
      if (filter.key === "date") this.dateFilter = "any";
      if (filter.key === "sort") this.sortMode = "manual";
    },
    clearFilters() {
      this.searchQuery = "";
      this.selectedProjectId = null;
      this.selectedTag = "";
      this.selectedPriority = "all";
      this.dateFilter = "any";
      this.sortMode = "manual";
    },
    applyQuickFilter(type) {
      if (type === "overdue") this.dateFilter = "overdue";
      if (type === "today") this.dateFilter = "today";
      if (type === "week") this.dateFilter = "week";
      if (type === "high") this.selectedPriority = "high";
    },
    setView(view) {
      this.currentView = view;
      if (view === "calendar" && !this.calendarMonth) {
        this.calendarMonth = this.toMonthKey(new Date());
      }
    },
    toggleCompact() {
      this.compactMode = !this.compactMode;
    },
    toggleFormDone() {
      this.taskForm.done = !this.taskForm.done;
    },
    shiftCalendar(direction) {
      const current = this.calendarMonthDate;
      const next = new Date(current.getFullYear(), current.getMonth() + direction, 1);
      this.calendarMonth = this.toMonthKey(next);
    },
    resetCalendar() {
      this.calendarMonth = this.toMonthKey(new Date());
    },
    async loadBoard() {
      this.loading = true;
      this.error = "";
      try {
        const board = await fetchTaskBoard();
        this.lanes = (board?.lanes || []).map((lane) => ({
          ...lane,
          tasks: (lane.tasks || [])
            .map((task) => this.normalizeTask(task))
            .sort((a, b) => a.position - b.position),
        }));
        this.projects = board?.projects || [];
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el tablero.";
      } finally {
        this.loading = false;
      }
    },
    async createNewLane() {
      const name = this.newLaneName.trim();
      if (!name) return;
      try {
        const lane = await createLane(name);
        this.lanes.push({ ...lane, tasks: [] });
        this.newLaneName = "";
      } catch (err) {
        this.error = err?.message || "No se pudo crear el carril.";
      }
    },
    editLane(lane) {
      this.editingLaneId = lane.id;
      this.editingLaneName = lane.name;
    },
    async saveLaneName(lane) {
      if (this.editingLaneId !== lane.id) return;
      const name = this.editingLaneName.trim();
      this.editingLaneId = null;
      if (!name || name === lane.name) return;
      try {
        const updated = await updateLane(lane.id, { name });
        lane.name = updated.name;
      } catch (err) {
        this.error = err?.message || "No se pudo actualizar el carril.";
      }
    },
    async removeLane(lane) {
      if (!confirm(`¿Eliminar el carril "${lane.name}" y sus tareas?`)) return;
      try {
        await deleteLane(lane.id);
        this.lanes = this.lanes.filter((item) => item.id !== lane.id);
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar el carril.";
      }
    },
    toggleLaneCollapse(laneId) {
      const list = new Set(this.collapsedLaneIds || []);
      if (list.has(laneId)) {
        list.delete(laneId);
      } else {
        list.add(laneId);
      }
      this.collapsedLaneIds = Array.from(list);
      this.persistPreferences();
    },
    isLaneCollapsed(laneId) {
      return (this.collapsedLaneIds || []).includes(laneId);
    },
    openTaskModal(laneId, task) {
      const lane = laneId || this.lanes[0]?.id;
      if (!lane) return;
      if (task) {
        const tags = this.taskTags(task);
        this.taskForm = {
          id: task.id,
          title: task.title,
          description: task.description || "",
          laneId: laneId,
          done: task.done,
          projectId: task.projectId || null,
          priority: this.normalizePriority(task.priority) || "",
          tagsInput: tags.join(", "),
          dueDate: this.formatDateInput(task.dueDate),
          assigneeName: this.taskAssignee(task),
          progress: this.taskProgress(task) ?? 0,
        };
        this.projectSearch = task.projectName || "";
      } else {
        this.taskForm = {
          id: null,
          title: "",
          description: "",
          laneId: lane,
          done: false,
          projectId: null,
          priority: "",
          tagsInput: "",
          dueDate: "",
          assigneeName: "",
          progress: 0,
        };
        this.projectSearch = "";
      }
      this.showTaskModal = true;
    },
    closeTaskModal() {
      this.showTaskModal = false;
    },
    async saveTask() {
      const title = this.taskForm.title.trim();
      if (!title) return;
      try {
        await this.ensureProjectSelected();
        const tags = this.parseTagsInput(this.taskForm.tagsInput);
        const progress =
          this.taskForm.progress == null ? null : Math.max(0, Math.min(100, this.taskForm.progress));
        if (this.taskForm.id) {
          const updated = await updateTask(this.taskForm.id, {
            title,
            description: this.taskForm.description,
            laneId: this.taskForm.laneId,
            done: this.taskForm.done,
            projectId: this.taskForm.projectId,
            priority: this.taskForm.priority || null,
            tags,
            dueDate: this.taskForm.dueDate || null,
            assigneeName: this.taskForm.assigneeName || null,
            progress,
          });
          this.applyTaskUpdate(this.normalizeTask(updated));
        } else {
          const created = await createTask({
            title,
            description: this.taskForm.description,
            laneId: this.taskForm.laneId,
            projectId: this.taskForm.projectId,
            priority: this.taskForm.priority || null,
            tags,
            dueDate: this.taskForm.dueDate || null,
            assigneeName: this.taskForm.assigneeName || null,
            progress,
          });
          const normalized = this.normalizeTask(created);
          const laneId = normalized.laneId || this.taskForm.laneId;
          const lane = this.lanes.find((item) => item.id === laneId);
          if (lane) {
            lane.tasks.push(normalized);
            lane.tasks.sort((a, b) => a.position - b.position);
          }
        }
        this.closeTaskModal();
      } catch (err) {
        this.error = err?.message || "No se pudo guardar la tarea.";
      }
    },
    async removeTask() {
      if (!this.taskForm.id) return;
      if (!confirm("¿Eliminar esta tarea?")) return;
      try {
        await deleteTask(this.taskForm.id);
        this.lanes.forEach((lane) => {
          lane.tasks = lane.tasks.filter((task) => task.id !== this.taskForm.id);
        });
        this.closeTaskModal();
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar la tarea.";
      }
    },
    applyTaskUpdate(updated) {
      const normalized = this.normalizeTask(updated);
      let targetLane = this.lanes.find((lane) => lane.id === normalized.laneId);
      this.lanes.forEach((lane) => {
        lane.tasks = lane.tasks.filter((task) => task.id !== normalized.id);
      });
      if (targetLane) {
        targetLane.tasks.push(normalized);
        targetLane.tasks.sort((a, b) => a.position - b.position);
      }
    },
    async toggleDone(task) {
      const previous = task.done;
      task.done = !task.done;
      try {
        const updated = await updateTask(task.id, { done: task.done });
        this.applyTaskUpdate(this.normalizeTask(updated));
      } catch (err) {
        task.done = previous;
        this.error = err?.message || "No se pudo actualizar la tarea.";
      }
    },
    async duplicateTask(laneId, task) {
      try {
        const tags = this.taskTags(task);
        const created = await createTask({
          title: `${task.title} (copia)`,
          description: task.description || "",
          laneId: laneId,
          projectId: task.projectId || null,
          priority: task.priority || null,
          tags,
          dueDate: task.dueDate || null,
          assigneeName: this.taskAssignee(task) || null,
          progress: this.taskProgress(task),
        });
        const normalized = this.normalizeTask(created);
        const lane = this.lanes.find((item) => item.id === laneId);
        if (lane) {
          lane.tasks.push(normalized);
          lane.tasks.sort((a, b) => a.position - b.position);
        }
      } catch (err) {
        this.error = err?.message || "No se pudo duplicar la tarea.";
      }
    },
    async loadProjects() {
      try {
        this.projects = await fetchTaskProjects();
      } catch (_) {
        this.projects = [];
      }
    },
    openProjectOptions() {
      this.showProjectOptions = true;
    },
    closeProjectOptions() {
      setTimeout(() => {
        this.showProjectOptions = false;
      }, 120);
    },
    selectProject(project) {
      this.projectSearch = project.name;
      this.taskForm.projectId = project.id;
      this.showProjectOptions = false;
    },
    onProjectInput() {
      const match = this.projects.find(
        (project) => project.name.toLowerCase() === this.projectSearch.trim().toLowerCase()
      );
      this.taskForm.projectId = match ? match.id : null;
    },
    async createProjectFromInput() {
      const name = this.projectSearch.trim();
      if (!name) return;
      try {
        const project = await createTaskProject(name);
        if (!this.projects.some((item) => item.id === project.id)) {
          this.projects.push(project);
        }
        this.taskForm.projectId = project.id;
        this.projectSearch = project.name;
      } catch (err) {
        this.error = err?.message || "No se pudo crear el proyecto.";
      }
    },
    async ensureProjectSelected() {
      const name = this.projectSearch.trim();
      if (!name) {
        this.taskForm.projectId = null;
        return;
      }
      const match = this.projects.find(
        (project) => project.name.toLowerCase() === name.toLowerCase()
      );
      if (match) {
        this.taskForm.projectId = match.id;
        return;
      }
      const project = await createTaskProject(name);
      if (!this.projects.some((item) => item.id === project.id)) {
        this.projects.push(project);
      }
      this.taskForm.projectId = project.id;
      this.projectSearch = project.name;
    },
    onTaskDragStart(task, laneId, event) {
      if (!this.canDrag) return;
      this.draggingTask = { taskId: task.id, laneId };
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(task.id));
      }
    },
    onTaskDragOver(laneId, index) {
      if (!this.canDrag) return;
      this.dragOver = { laneId, index };
      this.dragOverLaneId = laneId;
    },
    async onTaskDrop(laneId) {
      if (!this.canDrag || !this.draggingTask) return;
      const fromLane = this.lanes.find((lane) => lane.id === this.draggingTask.laneId);
      const toLane = this.lanes.find((lane) => lane.id === laneId);
      if (!fromLane || !toLane) return;
      const fromVisibleBefore = this.getVisibleTasks(fromLane);
      const draggedVisibleIndex = fromVisibleBefore.findIndex(
        (task) => task.id === this.draggingTask.taskId
      );
      const taskIndex = fromLane.tasks.findIndex((task) => task.id === this.draggingTask.taskId);
      if (taskIndex === -1) return;
      const [task] = fromLane.tasks.splice(taskIndex, 1);
      task.laneId = laneId;

      const sameLane = fromLane.id === toLane.id;
      let targetIndex =
        this.dragOver && this.dragOver.laneId === laneId ? this.dragOver.index : null;
      if (targetIndex == null) {
        targetIndex = this.getVisibleTasks(toLane).length;
      }
      if (sameLane && draggedVisibleIndex !== -1 && targetIndex > draggedVisibleIndex) {
        targetIndex -= 1;
      }

      const visibleIndices = this.collectVisibleIndices(toLane.tasks);
      const insertIndex =
        targetIndex >= visibleIndices.length ? toLane.tasks.length : visibleIndices[targetIndex];
      toLane.tasks.splice(insertIndex, 0, task);

      await this.syncTaskOrder(sameLane ? [toLane] : [fromLane, toLane]);
      this.draggingTask = null;
      this.dragOver = null;
      this.dragOverLaneId = null;
    },
    async syncTaskOrder(lanes) {
      const updates = lanes.flatMap((lane) =>
        lane.tasks.map((task, index) => ({
          taskId: task.id,
          laneId: lane.id,
          position: index,
        }))
      );
      try {
        await reorderTasks(updates);
      } catch (err) {
        this.error = err?.message || "No se pudo reordenar.";
      }
    },
    onLaneDragStart(laneId, event) {
      if (!this.canDrag || this.draggingTask) return;
      if (event?.target?.closest?.(".kanban-card")) return;
      this.draggingLaneId = laneId;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(laneId));
      }
    },
    onTaskDragEnd() {
      this.draggingTask = null;
      this.dragOver = null;
      this.dragOverLaneId = null;
    },
    onLaneDragEnd() {
      this.draggingLaneId = null;
      this.laneDropIndex = null;
    },
    onLaneDragOver(index) {
      if (!this.canDrag) return;
      this.laneDropIndex = index;
    },
    async onLaneDrop() {
      if (!this.canDrag || this.draggingLaneId == null || this.laneDropIndex == null) return;
      const fromIndex = this.lanes.findIndex((lane) => lane.id === this.draggingLaneId);
      if (fromIndex === -1) return;
      const [lane] = this.lanes.splice(fromIndex, 1);
      this.lanes.splice(this.laneDropIndex, 0, lane);
      this.draggingLaneId = null;
      this.laneDropIndex = null;
      try {
        await reorderLanes(this.lanes.map((item) => item.id));
      } catch (err) {
        this.error = err?.message || "No se pudo reordenar los carriles.";
      }
    },
    isDragging(task, lane) {
      return this.draggingTask?.taskId === task.id && this.draggingTask?.laneId === lane.id;
    },
    isDragOverLane(laneId) {
      return this.dragOverLaneId === laneId;
    },
    isLaneDragTarget(index) {
      return this.draggingLaneId != null && this.laneDropIndex === index;
    },
    isTaskVisible(task) {
      return this.filterTasks([task]).length > 0;
    },
    getVisibleTasks(lane) {
      return this.filterTasks(lane.tasks);
    },
    collectVisibleIndices(tasks) {
      const indices = [];
      tasks.forEach((task, index) => {
        if (this.isTaskVisible(task)) {
          indices.push(index);
        }
      });
      return indices;
    },
    filterTasks(tasks) {
      const query = this.searchQueryDebounced.trim().toLowerCase();
      return tasks.filter((task) => {
        if (this.selectedProjectId && String(task.projectId) !== String(this.selectedProjectId)) {
          return false;
        }
        if (query) {
          const tags = this.taskTags(task).join(" ");
          const assignee = this.taskAssignee(task);
          const haystack = [task.title, task.description, task.projectName, tags, assignee]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        if (this.selectedTag) {
          const tags = this.taskTags(task);
          if (!tags.includes(this.selectedTag)) return false;
        }
        if (this.selectedPriority !== "all") {
          const normalized = this.normalizePriority(task.priority);
          if (normalized !== this.selectedPriority) return false;
        }
        if (this.dateFilter !== "any") {
          const due = this.parseDate(task.dueDate);
          if (this.dateFilter === "nodate" && due) return false;
          if (this.dateFilter === "nodate" && !due) return true;
          if (!due) return false;
          if (this.dateFilter === "overdue" && !this.isOverdue(task)) return false;
          if (this.dateFilter === "today" && !this.isToday(due)) return false;
          if (this.dateFilter === "week" && !this.isWithinDays(due, 7)) return false;
        }
        return true;
      });
    },
    sortTasks(tasks, mode) {
      if (mode === "manual") return tasks;
      const sorted = [...tasks];
      if (mode === "updated") {
        sorted.sort((a, b) => new Date(b.updated || b.created) - new Date(a.updated || a.created));
      }
      if (mode === "due") {
        sorted.sort((a, b) => {
          const ad = this.parseDate(a.dueDate);
          const bd = this.parseDate(b.dueDate);
          if (!ad && !bd) return 0;
          if (!ad) return 1;
          if (!bd) return -1;
          return ad - bd;
        });
      }
      if (mode === "priority") {
        sorted.sort((a, b) => this.priorityRank(b) - this.priorityRank(a));
      }
      return sorted;
    },
    parseDate(value) {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    toDateKey(date) {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    toMonthKey(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      return `${year}-${month}`;
    },
    startOfDay(date) {
      const copy = new Date(date);
      copy.setHours(0, 0, 0, 0);
      return copy;
    },
    isToday(date) {
      const today = this.startOfDay(new Date());
      const target = this.startOfDay(date);
      return today.getTime() === target.getTime();
    },
    isWithinDays(date, days) {
      const today = this.startOfDay(new Date());
      const future = new Date(today);
      future.setDate(today.getDate() + days);
      return date >= today && date <= future;
    },
    isOverdue(task) {
      const due = this.parseDate(task.dueDate);
      if (!due || task.done) return false;
      const today = this.startOfDay(new Date());
      return due < today;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("es-ES", {
        dateStyle: "medium",
      }).format(new Date(value));
    },
    formatShortDate(value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("es-ES", {
        day: "2-digit",
        month: "short",
      }).format(new Date(value));
    },
    formatDateInput(value) {
      const date = this.parseDate(value);
      return date ? this.toDateKey(date) : "";
    },
    formatDueDate(task) {
      const due = this.parseDate(task.dueDate);
      if (!due) return "";
      return this.formatShortDate(due);
    },
    parseTagsInput(input) {
      if (!input) return [];
      if (Array.isArray(input)) return input.filter(Boolean);
      return String(input)
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    },
    normalizePriority(value) {
      if (value == null) return null;
      const normalized = String(value).toLowerCase();
      if (["high", "alta", "p1", "1", "urgent"].includes(normalized)) return "high";
      if (["medium", "media", "p2", "2"].includes(normalized)) return "medium";
      if (["low", "baja", "p3", "3"].includes(normalized)) return "low";
      return null;
    },
    priorityLabel(task) {
      const normalized = this.normalizePriority(task.priority);
      if (normalized === "high") return "Alta";
      if (normalized === "medium") return "Media";
      if (normalized === "low") return "Baja";
      return "";
    },
    priorityClass(task) {
      const normalized = this.normalizePriority(task.priority);
      if (normalized === "high") return "priority-high";
      if (normalized === "medium") return "priority-medium";
      if (normalized === "low") return "priority-low";
      return "";
    },
    priorityRank(task) {
      const normalized = this.normalizePriority(task.priority);
      if (normalized === "high") return 3;
      if (normalized === "medium") return 2;
      if (normalized === "low") return 1;
      return 0;
    },
    taskTags(task) {
      const tags = task.tags || task.tagList || task.labels || [];
      if (Array.isArray(tags)) return tags.filter(Boolean);
      if (typeof tags === "string") {
        return tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean);
      }
      return [];
    },
    taskAssignee(task) {
      return task.assigneeName || task.assignee || task.owner || "";
    },
    taskProgress(task) {
      const value = task.progress ?? task.completion ?? null;
      if (value == null) return null;
      const number = Number(value);
      if (Number.isNaN(number)) return null;
      return Math.max(0, Math.min(100, Math.round(number)));
    },
    laneTone(lane) {
      const name = (typeof lane === "string" ? lane : lane.name || "").toLowerCase();
      if (name.includes("pend")) return "tone-pending";
      if (name.includes("progreso") || name.includes("doing")) return "tone-progress";
      if (name.includes("hecho") || name.includes("done")) return "tone-done";
      return "tone-neutral";
    },
    normalizeTask(task) {
      if (!task) return task;
      return {
        ...task,
        laneId: task.lane_id ?? task.laneId,
        projectId: task.project_id ?? task.projectId ?? null,
        projectName: task.project_name ?? task.projectName ?? null,
        priority: task.priority ?? task.priority_level ?? task.priorityLevel ?? null,
        dueDate: task.due_date ?? task.dueDate ?? null,
        assigneeName: task.assignee_name ?? task.assigneeName ?? null,
        tags: task.tags ?? task.tag_list ?? task.tagList ?? task.labels ?? [],
        progress: task.progress ?? task.completion ?? null,
        created: task.created,
        updated: task.updated,
      };
    },
  },
};
</script>

<style scoped>
.tasks-board {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: calc(100vh - 160px);
}

.board-shell {
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  gap: 14px;
  padding-bottom: 12px;
  background: linear-gradient(180deg, rgba(11, 18, 32, 0.98), rgba(11, 18, 32, 0.65));
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
  backdrop-filter: blur(12px);
}

.board-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: start;
}

.board-title h1 {
  margin: 0 0 6px;
  font-size: 24px;
  letter-spacing: -0.01em;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.board-count {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.16);
  border: 1px solid rgba(56, 189, 248, 0.4);
  color: var(--text);
}

.muted {
  color: var(--muted);
  margin: 0 0 10px;
  font-size: 13px;
}

.board-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.meta-chip {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.1);
}

.meta-chip.active {
  border-color: rgba(56, 189, 248, 0.45);
  background: rgba(56, 189, 248, 0.14);
}

.quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
}

.chip.active {
  border-color: rgba(56, 189, 248, 0.6);
  background: rgba(56, 189, 248, 0.2);
}

.board-actions {
  display: grid;
  gap: 12px;
  justify-items: end;
}

.view-toggle {
  display: inline-flex;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  padding: 4px;
  gap: 6px;
}

.toggle {
  background: transparent;
  border: none;
  color: var(--muted);
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.toggle.active {
  background: rgba(56, 189, 248, 0.2);
  color: var(--text);
}

.action-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.lane-create {
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--panel);
  border: 1px solid var(--border);
  padding: 8px 10px;
  border-radius: 12px;
}

.lane-create input {
  border: none;
  background: transparent;
  color: var(--text);
  outline: none;
}

.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
}

.primary {
  background: linear-gradient(120deg, var(--accent), var(--success));
  color: #071022;
  border: none;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.board-scroll {
  flex: 1;
  min-height: 0;
}

.lanes-inner {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(280px, 1fr);
  gap: 18px;
  padding-bottom: 16px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
}

.lane {
  background: var(--column-bg);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 12px 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 420px;
  max-height: calc(100vh - 260px);
  box-shadow: var(--shadow);
  scroll-snap-align: start;
  position: relative;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.lane::before {
  content: "";
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  height: 4px;
  border-radius: 16px 16px 0 0;
  background: var(--lane-accent, rgba(56, 189, 248, 0.6));
}

.lane.tone-pending {
  --lane-accent: rgba(148, 163, 184, 0.7);
}

.lane.tone-progress {
  --lane-accent: rgba(56, 189, 248, 0.75);
}

.lane.tone-done {
  --lane-accent: rgba(52, 211, 153, 0.8);
}

.lane.tone-neutral {
  --lane-accent: rgba(148, 163, 184, 0.6);
}

.lane.drop-target {
  border-color: rgba(56, 189, 248, 0.6);
}

.lane-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  position: sticky;
  top: 0;
  background: var(--column-bg);
  padding-top: 6px;
  z-index: 1;
}

.lane-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.lane-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--lane-accent, rgba(56, 189, 248, 0.6));
}

.lane-title h3 {
  margin: 0;
  font-size: 15px;
  cursor: pointer;
}

.lane-input {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 8px;
  color: var(--text);
}

.count {
  font-size: 12px;
  background: rgba(56, 189, 248, 0.2);
  padding: 2px 8px;
  border-radius: 999px;
}

.count.subtle {
  background: transparent;
  color: var(--muted);
  padding: 0;
}

.lane-actions {
  display: flex;
  gap: 6px;
}

.icon {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
}

.icon.danger {
  color: var(--danger);
}

.lane-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  min-height: 0;
  padding-right: 4px;
}

.lane-body.drag-over {
  background: rgba(56, 189, 248, 0.08);
  border-radius: 14px;
}

.lane-body.collapsed {
  display: none;
}

.cards {
  display: grid;
  gap: 12px;
}

.list-view {
  background: var(--kanban-bg);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 12px;
}

.list-table {
  display: grid;
  gap: 8px;
}

.list-row {
  display: grid;
  grid-template-columns: 2fr 1fr 0.9fr 0.9fr 1fr 0.8fr 1fr 0.5fr;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid transparent;
  background: var(--panel-2);
  color: var(--text);
  cursor: pointer;
  text-align: left;
}

.list-row.header {
  background: transparent;
  border-color: transparent;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: default;
}

.list-row:not(.header):hover {
  border-color: rgba(56, 189, 248, 0.4);
  background: rgba(20, 33, 59, 0.9);
}

.list-row:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.6);
  outline-offset: 2px;
}

.cell-title {
  display: grid;
  gap: 4px;
}

.cell-title small {
  color: var(--muted);
  font-size: 11px;
}

.lane-pill {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.16);
  border: 1px solid rgba(148, 163, 184, 0.3);
}

.lane-pill.tone-pending {
  border-color: rgba(148, 163, 184, 0.5);
}

.lane-pill.tone-progress {
  border-color: rgba(56, 189, 248, 0.6);
}

.lane-pill.tone-done {
  border-color: rgba(52, 211, 153, 0.6);
}

.priority-pill {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.12);
}

.priority-pill.priority-high {
  color: #fca5a5;
  border-color: rgba(248, 113, 113, 0.55);
  background: rgba(248, 113, 113, 0.18);
}

.priority-pill.priority-medium {
  color: #fcd34d;
  border-color: rgba(251, 191, 36, 0.55);
  background: rgba(251, 191, 36, 0.2);
}

.priority-pill.priority-low {
  color: #7dd3fc;
  border-color: rgba(56, 189, 248, 0.55);
  background: rgba(56, 189, 248, 0.18);
}

.row-actions {
  display: flex;
  justify-content: flex-end;
}

.icon.tiny {
  width: 32px;
  height: 28px;
  border-radius: 999px;
  font-size: 11px;
}

.overdue {
  color: var(--danger);
}

.calendar-view {
  display: grid;
  gap: 16px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.calendar-header h3 {
  margin: 0;
  text-transform: capitalize;
}

.calendar-controls {
  display: flex;
  gap: 8px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;
}

.calendar-weekday {
  text-align: center;
  font-size: 12px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.calendar-cell {
  min-height: 110px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 14px;
  padding: 8px;
  background: var(--panel-2);
  display: grid;
  gap: 6px;
  align-content: start;
}

.calendar-cell.today {
  border-color: rgba(56, 189, 248, 0.6);
}

.calendar-cell.muted {
  opacity: 0.55;
}

.cell-head {
  font-size: 12px;
  font-weight: 600;
}

.cell-tasks {
  display: grid;
  gap: 6px;
}

.calendar-pill {
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: var(--text);
  border-radius: 10px;
  padding: 4px 6px;
  font-size: 11px;
  text-align: left;
  cursor: pointer;
  display: block;
}

.calendar-pill:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.7);
  outline-offset: 2px;
}

.calendar-pill.priority-high {
  border-color: rgba(248, 113, 113, 0.45);
  background: rgba(248, 113, 113, 0.18);
}

.calendar-pill.priority-medium {
  border-color: rgba(251, 191, 36, 0.45);
  background: rgba(251, 191, 36, 0.18);
}

.calendar-pill.priority-low {
  border-color: rgba(56, 189, 248, 0.45);
  background: rgba(56, 189, 248, 0.16);
}

.calendar-pill.overdue {
  border-color: rgba(248, 113, 113, 0.6);
}

.calendar-unscheduled {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 14px;
  padding: 12px;
  background: var(--panel);
}

.calendar-unscheduled h4 {
  margin: 0 0 8px;
}

.unscheduled-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ghost-pill {
  background: rgba(148, 163, 184, 0.14);
  border-color: rgba(148, 163, 184, 0.3);
}

.lane-empty {
  text-align: center;
  color: var(--muted);
  border: 1px dashed rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  padding: 18px 10px;
}

.card-skeleton {
  background: var(--panel-2);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 14px;
  padding: 12px;
  display: grid;
  gap: 10px;
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(480px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
}

.modal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.modal-body input[type="file"] {
  display: block;
  min-width: 0;
}

.project-field {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
  position: relative;
}

.project-field input {
  flex: 1;
}

.project-options {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 6px;
  display: grid;
  gap: 4px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 4;
  box-shadow: var(--shadow);
}

.project-option {
  background: var(--panel-2);
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 8px 10px;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}

.project-option:hover {
  border-color: rgba(56, 189, 248, 0.5);
  background: rgba(56, 189, 248, 0.12);
}

.project-empty {
  padding: 8px 10px;
  color: var(--muted);
  font-size: 12px;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.danger-text {
  background: transparent;
  border: none;
  color: var(--danger);
  cursor: pointer;
  text-align: left;
  font-size: 13px;
}

.checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox input {
  display: none;
}

.checkbox span {
  width: 16px;
  height: 16px;
  border: 1px solid var(--border);
  border-radius: 4px;
  display: inline-block;
  position: relative;
}

.checkbox input:checked + span {
  background: rgba(34, 197, 94, 0.4);
  border-color: rgba(34, 197, 94, 0.6);
}

.checkbox.line {
  margin-top: 8px;
}

.lane-enter-active,
.lane-leave-active,
.card-enter-active,
.card-leave-active {
  transition: all 0.2s ease;
}

.lane-enter-from,
.lane-leave-to,
.card-enter-from,
.card-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 1100px) {
  .board-header {
    grid-template-columns: 1fr;
  }

  .board-actions {
    justify-items: start;
  }

  .view-toggle {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 900px) {
  .lanes-inner {
    grid-auto-columns: minmax(240px, 1fr);
  }

  .board-shell {
    position: static;
  }

  .list-row {
    grid-template-columns: 1fr 0.8fr 0.8fr 0.8fr;
    grid-auto-rows: auto;
  }

  .list-row span:nth-child(n + 5) {
    display: none;
  }

  .calendar-grid {
    grid-template-columns: repeat(7, minmax(120px, 1fr));
    overflow-x: auto;
    padding-bottom: 8px;
  }

  .modal-grid {
    grid-template-columns: 1fr;
  }
}
</style>
